/**
 * The timeline axis under the map, in screen space: the time span of the
 * cards in view, ticks that close in with the zoom, the report rounds as
 * flags, the in-view cards' dates as marks, and the hovered card's date
 * picked out. All geometry from timeAxis.js; this only draws it.
 */
import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { spanInView, viewRect, chooseTicks, timeToX } from './timeAxis';

export const AXIS_HEIGHT = 64;
const MARGIN = 24;
const LOCALES = { fi: 'fi-FI', en: 'en-GB', sv: 'sv-SE' };
const ACCENT = '#4cc7ce';
const FLAG = '#f0b35e';

function useFormat() {
  const { t, language } = useTranslation();
  const locale = LOCALES[language] || 'fi-FI';
  const month = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const tick = (k) => {
    if (k.unit === 'year') return String(k.year);
    const base = k.unit === 'quarter' ? t('impactPath.scene.axis.quarter', { n: k.quarter })
      : k.unit === 'month' ? month.format(k.t)
        : t('impactPath.scene.axis.week', { n: k.week });
    return k.newYear ? `${base} ${k.year}` : base;
  };
  return { t, tick, date: (ms) => day.format(ms) };
}

export default function TimelineAxis({ cards, times, rounds, transform, width, height, hovered }) {
  const { t, tick, date } = useFormat();
  const axisW = Math.max(0, width - 2 * MARGIN);
  const span = transform ? spanInView(cards, times, viewRect(transform, width, height - AXIS_HEIGHT)) : null;
  if (!span) return <Bar><Empty>{t('impactPath.scene.axis.noDates')}</Empty></Bar>;
  const { ticks } = chooseTicks(span, axisW);
  const x = (ms) => MARGIN + timeToX(ms, span, axisW);
  const inSpan = (ms) => ms >= span.start && ms <= span.end;
  const rect = viewRect(transform, width, height - AXIS_HEIGHT);
  const visible = cards.filter((c) => times.has(c.id) && c.x + c.w >= rect.x && c.x <= rect.x + rect.w && c.y + c.h >= rect.y && c.y <= rect.y + rect.h);
  const hot = hovered ? times.get(hovered) : null;
  // A round's word only where it has room before the next flag; the flag and
  // its tooltip are always there.
  const flags = rounds.filter((r) => inSpan(r.t)).map((r, i, all) => ({
    ...r,
    labelled: r.round != null && (i === all.length - 1 || x(all[i + 1].t) - x(r.t) > 64),
  }));
  return (
    <Bar>
      <svg width={width} height={AXIS_HEIGHT}>
        <line x1={MARGIN} x2={width - MARGIN} y1={30} y2={30} stroke="#5b6478" />
        {visible.map((c) => {
          const iv = times.get(c.id);
          return iv.point
            ? <circle key={c.id} cx={x(iv.start)} cy={24} r={2.5} fill="#c8cfdc" opacity={0.7} />
            : <rect key={c.id} x={x(iv.start)} y={26} width={Math.max(1, x(iv.end) - x(iv.start))} height={2} fill="#c8cfdc" opacity={0.18} />;
        })}
        {ticks.map((k) => (
          <g key={k.t} transform={`translate(${x(k.t)}, 30)`}>
            <line y1={0} y2={k.newYear || k.unit === 'year' ? 10 : 6} stroke="#8a93a6" />
            <text y={22} textAnchor="middle" fontSize={11} fill="#c8cfdc" fontWeight={k.newYear ? 700 : 400}>{tick(k)}</text>
          </g>
        ))}
        {flags.map((r) => (
          <g key={r.t} transform={`translate(${x(r.t)}, 30)`}>
            <title>{t('impactPath.scene.axis.report', { date: date(r.t), count: r.count })}</title>
            <line y1={0} y2={-20} stroke={FLAG} strokeWidth={1.2} />
            <path d="M 0 -20 L 8 -17 L 0 -14 z" fill={FLAG} />
            {r.labelled && <text x={10} y={-14} fontSize={9} fill={FLAG}>{t('impactPath.scene.axis.round', { n: r.round })}</text>}
          </g>
        ))}
        {hot && (
          <g pointerEvents="none">
            {hot.point
              ? <circle cx={x(hot.start)} cy={30} r={6} fill={ACCENT} />
              : <rect x={x(hot.start)} y={26} width={Math.max(3, x(hot.end) - x(hot.start))} height={8} rx={4} fill={ACCENT} opacity={0.85} />}
            <text x={x(hot.end)} y={12} textAnchor="middle" fontSize={11} fontWeight={700} fill={ACCENT}>
              {t(`impactPath.scene.axis.basis.${hot.basis || 'stated'}`, { date: date(hot.end) })}
            </text>
          </g>
        )}
      </svg>
    </Bar>
  );
}

const Bar = styled.div`
  position: fixed; left: 0; right: 0; bottom: 0; height: ${AXIS_HEIGHT}px;
  z-index: 5; /* above the cards' HTML, which composites over the canvas */
  background: #0f121a; border-top: 1px solid #2a3040; pointer-events: auto;
`;

const Empty = styled.div`
  height: 100%; display: flex; align-items: center; justify-content: center; color: #8a93a6; font-size: 12px;
`;
